import React from "react";
import {
  Box, Grid, Typography, Button, InputBase, Tabs, Tab, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Snackbar, Alert, IconButton, Chip,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import { tokens } from "../../theme";
import { playerProfiles } from "../../data/mockData";
import { getAllData, addData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import PlayerRiskCard from "../../screens/User/UserList/PlayerRiskCard";
import moment from "moment-timezone";

const mono = '"IBM Plex Mono", monospace';
const disp = '"Space Grotesk", sans-serif';
const FILL = ["#8B6DF0", "#23D6A0", "#F0B43D", "#5BA8F5", "#F25C66", "#E86FB0"];
const initials = (n) => n.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const num = (v) => Number(v) || 0;

const ACTIONS = {
  grant: { title: "Grant SC", cta: "Grant", color: "primary" },
  adjust: { title: "Adjust Coins", cta: "Apply", color: "primary" },
  enrol: { title: "Enrol in journey", cta: "Enrol", color: "primary" },
  flag: { title: "Flag player", cta: "Flag", color: "error" },
};

// Map a raw /users record to the full profile shape (identity from API,
// rich profile fields fall back to a demo template so the layout matches the reference).
function mapUser(u, i) {
  const tmpl = playerProfiles[i % playerProfiles.length];
  const fullName = [u.name, u.surname].filter(Boolean).join(" ").trim();
  const rawKyc = String(u.kycStatus ?? u.kyc ?? "").toLowerCase();
  const kyc = /verified|cleared|approved|^1$|^2$|^3$/.test(rawKyc) ? "cleared" : rawKyc || "pending";
  return {
    ...tmpl,
    id: String(u.idUser ?? u.userId ?? u.id ?? `PL-${i}`),
    name: fullName || u.login || u.username || "Player",
    email: u.email || "—",
    state: u.stateName || u.state || tmpl.state,
    seg: u.playerType || tmpl.seg,
    vip: u.rgType && u.rgType !== "0" ? u.rgType : tmpl.vip,
    kyc,
    ctier: tmpl.ctier || "T1·US",
    rg: "Clear",
    risk: i % 7 === 6 ? "velocity" : null,
    bonusW: tmpl.bonusW ?? 42,
    bonusExp: tmpl.bonusExp ?? "3d",
    gc: num(u.gc_balance) || tmpl.gc,
    sc: num(u.sc_balance) || tmpl.sc,
    bonus: num(u.bonus_balance) || tmpl.bonus,
    scLiab: num(u.sc_liability) || tmpl.scLiab,
  };
}

// Demo-fill template defaults for the static mock profiles too.
const baseProfiles = playerProfiles.map((p, i) => ({
  ...p, ctier: "T1·US", rg: "Clear", risk: i % 7 === 6 ? "velocity" : null,
  bonusW: 42, bonusExp: "3d",
}));

function Guard({ lines }) {
  return (
    <Box sx={{ mt: 1.5, p: 1.3, borderRadius: "9px", border: `1px solid ${tokens.border}`, background: tokens.bg2 }}>
      <Typography sx={{ fontSize: 11, color: tokens.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, mb: 0.8 }}>Guardrails</Typography>
      {lines.map(([t, kind]) => (
        <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.3, fontSize: 12, color: kind === "warn" ? tokens.amber : tokens.secondary }}>
          {kind === "warn" ? <WarningAmberRoundedIcon sx={{ fontSize: 15 }} /> : <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />}
          {t}
        </Box>
      ))}
    </Box>
  );
}
function Maker({ children }) {
  return (
    <Box sx={{ mt: 1.3, display: "flex", gap: 1, fontSize: 11.5, color: tokens.amber, background: "rgba(245,166,35,0.09)", border: "1px solid rgba(245,166,35,0.3)", p: 1, borderRadius: "8px" }}>
      <WarningAmberRoundedIcon sx={{ fontSize: 15, flex: "none" }} /> {children}
    </Box>
  );
}

const kycPill = (kyc) => {
  if (kyc === "cleared") return { label: "CLEARED", bg: "rgba(35,214,160,0.13)", c: tokens.secondary };
  if (kyc === "pending") return { label: "PENDING", bg: "rgba(245,166,35,0.13)", c: tokens.amber };
  return { label: "NOT STARTED", bg: tokens.surface2, c: tokens.textDim };
};

export default function Players360() {
  const [players, setPlayers] = React.useState(baseProfiles);
  const [sel, setSel] = React.useState(baseProfiles[0]);
  const [q, setQ] = React.useState("");
  const [debouncedQ, setDebouncedQ] = React.useState("");
  const [apiLoaded, setApiLoaded] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState(0);
  const [noteText, setNoteText] = React.useState("");
  const [notes, setNotes] = React.useState([
    { actor: "NJ", text: "Granted goodwill SC after delayed withdrawal.", time: "Today 18:41" },
  ]);
  const [dialog, setDialog] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [snack, setSnack] = React.useState(null); // { text, sev }
  const [submitting, setSubmitting] = React.useState(false);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [detail, setDetail] = React.useState(null); // users/userid/{id} response
  const [playerTypes, setPlayerTypes] = React.useState([]); // /playertypes for Enrol

  const notify = (text, sev = "success") => setSnack({ text, sev });

  // Debounce the search box before hitting /users?search=…
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const url = debouncedQ
          ? `${endPoints.api.GET_USERS}?search=${encodeURIComponent(debouncedQ)}`
          : endPoints.api.GET_USERS; // GET /users
        const res = await getAllData(url);
        const arr = Array.isArray(res) ? res : res?.data || [];
        if (!active) return;
        if (Array.isArray(arr) && (arr.length || apiLoaded)) {
          const mapped = arr.slice(0, 200).map(mapUser);
          setApiLoaded(true);
          setPlayers(mapped);
          setSel((cur) => (mapped.some((p) => p.id === cur?.id) ? cur : mapped[0] || cur));
        }
      } catch (e) {
        /* keep demo fallback */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  // Load player types once (used by the Enrol dialog dropdown).
  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getAllData(endPoints.api.playertypes);
        const arr = Array.isArray(res) ? res : res?.data || [];
        if (active && Array.isArray(arr)) setPlayerTypes(arr);
      } catch (e) {
        /* ignore */
      }
    })();
    return () => { active = false; };
  }, []);

  // When a player is selected, load their full detail (wallet) + notes.
  const selectedId = sel?.id;
  React.useEffect(() => {
    const id = selectedId;
    if (!id || String(id).startsWith("PL-")) return; // skip demo-fallback ids
    let active = true;
    (async () => {
      try {
        const d = await getAllData(`${endPoints.api.GET_BY_USERID}/${id}`);
        if (active) setDetail(d && d.status === "success" ? d.data : null);
      } catch (e) { if (active) setDetail(null); }
      try {
        const n = await getAllData(`${endPoints.api.GET_NOTES}/${id}`);
        if (active && n && n.status === "success" && Array.isArray(n.data)) {
          setNotes(n.data.map((it) => ({
            actor: it.admin_name || it.createdBy || it.userName || "Admin",
            text: it.note || it.description || "",
            time: it.timestamp ? moment.utc(it.timestamp).local().format("MMM D, h:mm A") : "",
          })));
        } else if (active) {
          setNotes([]);
        }
      } catch (e) { if (active) setNotes([]); }
    })();
    return () => { active = false; };
  }, [selectedId, refreshTick]);

  // With the API loaded the search is done server-side (/users?search=…);
  // the demo fallback still filters locally.
  const list = apiLoaded ? players : players.filter((p) => {
    const f = q.trim().toLowerCase();
    return !f || p.name.toLowerCase().includes(f) || String(p.id).toLowerCase().includes(f) || (p.email || "").toLowerCase().includes(f);
  });

  const openDialog = (key) => { setForm({}); setDialog(key); };
  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Coin-type keys expected by admin/coin/{deposit,withdrawal}.
  const CURRENCY_COIN = { "Gold Coins": "gc_balance", "Saga Coins": "sc_credit" };

  const postCoin = async ({ coinType, transferType, amount, description }) => {
    const url = transferType === "WithDraw" ? endPoints.api.WITHDRAW_COIN : endPoints.api.DEPOSIT_COIN;
    const data = {
      userId: Number(sel.id),
      coinType,
      transferType,
      amount: String(amount),
      description: description || "",
      hideUserTrans: false,
    };
    const res = await addData(url, data);
    return res?.data;
  };

  const submitDialog = async () => {
    // Grant SC & Adjust hit the real coin API; Enrol/Flag stay stubbed for now.
    if (dialog === "grant" || dialog === "adjust") {
      const isGrant = dialog === "grant";
      const raw = Number(form.amount ?? (isGrant ? 25 : 100));
      if (!raw) { notify("Enter an amount", "error"); return; }
      const coinType = isGrant ? "sc_credit" : (CURRENCY_COIN[form.currency || "Gold Coins"] || "gc_balance");
      // Adjust supports negative values → Withdrawal
      const transferType = !isGrant && raw < 0 ? "WithDraw" : "Deposit";
      const description = form.reason || (isGrant ? "Grant SC" : "Balance adjustment");
      setSubmitting(true);
      try {
        const body = await postCoin({ coinType, transferType, amount: Math.abs(raw), description });
        if (body && body.status === "success") {
          notify(body.message || `${ACTIONS[dialog].cta} applied for ${sel.name}`, "success");
          setDialog(null);
          setRefreshTick((t) => t + 1); // reload wallet
        } else {
          notify(body?.message || "Request failed. Please try again.", "error");
        }
      } catch (e) {
        notify("Request failed. Please try again.", "error");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Enrol → update the player's player_type via /user/fieldupdate.
    if (dialog === "enrol") {
      const fieldValue = Number(form.playerType);
      if (!fieldValue) { notify("Select a player type", "error"); return; }
      const idAccount = detail?.account?.idAccount;
      const data = {
        idAccount,
        fieldValue,
        userId: Number(sel.id),
        fieldName: "player_type",
      };
      setSubmitting(true);
      try {
        const res = await addData(endPoints.api.userFieldUpdate, data);
        const body = res?.data;
        if (body && body.status === "success") {
          notify(body.message || `Player type updated for ${sel.name}`, "success");
          setDialog(null);
          setRefreshTick((t) => t + 1); // reload detail
        } else {
          notify(body?.message || "Update failed. Please try again.", "error");
        }
      } catch (e) {
        notify("Update failed. Please try again.", "error");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Flag — UI ready, API to follow.
    notify(`${ACTIONS[dialog].cta} — saved for ${sel.name} (API pending)`, "success");
    setDialog(null);
  };
  const addNote = () => { if (!noteText.trim()) return; setNotes((n) => [{ actor: "You", text: noteText.trim(), time: "now" }, ...n]); setNoteText(""); };

  const selIdx = players.findIndex((p) => p.id === sel.id);
  const avatarColor = FILL[(selIdx < 0 ? 0 : selIdx) % FILL.length];
  const kp = kycPill(sel.kyc);

  const acc = detail?.account || {};
  const hasDetail = !!detail;
  // When the detail API has loaded (real session), use its balances (even 0);
  // otherwise fall back to the demo template values.
  const gc = hasDetail ? num(acc.gc_balance ?? acc.gc_main) : sel.gc;
  const sc = hasDetail ? num(acc.sc_main ?? acc.sc_credit) : sel.sc;
  const bonus = hasDetail ? num(acc.sc_credit) : sel.bonus;
  const scLiab = hasDetail ? num(acc.sc_credit) : sel.scLiab;
  const wallet = [
    { l: "Gold Coins", tag: "GC", tagBg: "rgba(240,180,61,0.16)", tagC: tokens.accent, v: gc.toLocaleString(), sub: "entertainment only", bar: tokens.accent },
    { l: "Saga Coins", tag: "SC", tagBg: "rgba(35,214,160,0.16)", tagC: tokens.secondary, v: sc.toLocaleString(), sub: "redeemable", bar: tokens.secondary },
    { l: "Bonus coins", tag: "locked", tagBg: "rgba(232,111,176,0.16)", tagC: tokens.pink, v: bonus.toLocaleString(), sub: `${sel.bonusW}% wagered · exp ${sel.bonusExp}`, bar: tokens.pink, prog: sel.bonusW },
    { l: "SC liability", tag: "", v: `$${scLiab.toLocaleString()}`, sub: "granted, not redeemed", bar: tokens.secondary },
  ];

  return (
    <Box>
      <PageHeader
        title="Players 360"
        crumb="Audience / Players 360"
        subtitle={loading ? "Loading players…" : `Profile · in-flight offers · manual ops · ${players.length} players`}
      />

      <Grid container spacing={1.75} alignItems="flex-start">
        {/* ---------- Player list (from API) ---------- */}
        <Grid item xs={12} md={4} lg={3.2}>
          <SectionCard sx={{ p: 0 }}>
            <Box sx={{ p: 1.4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.2, height: 38, borderRadius: "8px", background: tokens.bg, border: `1px solid ${tokens.border}` }}>
                <SearchRoundedIcon sx={{ fontSize: 16, color: tokens.textDim }} />
                <InputBase placeholder="Search name, ID, email…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ color: tokens.text, fontSize: 12.5, flex: 1 }} />
              </Box>
            </Box>
            <Box sx={{ maxHeight: { md: "calc(100vh - 230px)" }, overflowY: "auto" }}>
              {list.length ? list.map((p) => {
                const i = players.indexOf(p);
                return (
                  <Box
                    key={p.id}
                    onClick={() => { setSel(p); setTab(0); }}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.2, p: "10px 13px", cursor: "pointer",
                      borderTop: `1px solid ${tokens.border}`,
                      borderLeft: sel.id === p.id ? `2px solid ${tokens.primary}` : "2px solid transparent",
                      background: sel.id === p.id ? tokens.selected : "transparent",
                      "&:hover": { background: tokens.hover },
                    }}
                  >
                    <Box sx={{ width: 34, height: 34, borderRadius: "9px", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 12, color: tokens.onAccent, bgcolor: FILL[i % FILL.length], flex: "none" }}>
                      {initials(p.name)}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{p.name}</Typography>
                      <Typography sx={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim }} noWrap>{p.id} · {p.state} · {p.seg}</Typography>
                    </Box>
                    {p.risk && <FlagRoundedIcon sx={{ fontSize: 15, color: tokens.danger }} />}
                  </Box>
                );
              }) : <Typography sx={{ p: 3, textAlign: "center", color: tokens.textDim, fontSize: 12 }}>No players match.</Typography>}
            </Box>
          </SectionCard>
        </Grid>

        {/* ---------- Profile ---------- */}
        <Grid item xs={12} md={8} lg={8.8}>
          {/* Hero */}
          <SectionCard sx={{ mb: 1.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
              <Box sx={{ width: 50, height: 50, borderRadius: "13px", display: "grid", placeItems: "center", fontFamily: disp, fontWeight: 700, fontSize: 17, color: tokens.onAccent, bgcolor: avatarColor }}>
                {initials(sel.name)}
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontFamily: disp, fontSize: 18, fontWeight: 600 }}>{sel.name}</Typography>
                  {sel.vip !== "—" && <Chip size="small" label={sel.vip} sx={{ bgcolor: "rgba(240,180,61,0.14)", color: tokens.accent, fontWeight: 600, height: 20 }} />}
                  {sel.risk && <Chip size="small" icon={<FlagRoundedIcon sx={{ fontSize: 13 }} />} label={sel.risk} sx={{ bgcolor: "rgba(242,92,102,0.13)", color: tokens.danger, fontWeight: 600, height: 20, "& .MuiChip-icon": { color: tokens.danger } }} />}
                </Box>
                <Typography sx={{ fontFamily: mono, fontSize: 12, color: tokens.textDim }}>{sel.id} · {sel.email}</Typography>
              </Box>
              <Box sx={{ ml: "auto", display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button size="small" variant="contained" onClick={() => openDialog("grant")}>Grant SC</Button>
                <Button size="small" variant="outlined" color="inherit" onClick={() => openDialog("adjust")}>Adjust</Button>
                <Button size="small" variant="outlined" color="inherit" onClick={() => openDialog("enrol")}>Enrol</Button>
                <Button size="small" variant="outlined" startIcon={<FlagRoundedIcon />} onClick={() => openDialog("flag")}
                  sx={{ color: tokens.danger, borderColor: "rgba(242,92,102,0.4)", "&:hover": { borderColor: tokens.danger, bgcolor: "rgba(242,92,102,0.1)" } }}>
                  Flag
                </Button>
              </Box>
            </Box>

            {/* 6-attr grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3,1fr)", sm: "repeat(6,1fr)" }, gap: "1px", mt: 2, background: tokens.border, border: `1px solid ${tokens.border}`, borderRadius: "10px", overflow: "hidden" }}>
              {[
                ["Segment", sel.seg, 12], ["Player level", sel.lvl, 12], ["VIP tier", sel.vip], ["Country tier", sel.ctier, 12], ["Lifetime deposits", sel.ltd], ["RG status", sel.rg, 12, tokens.secondary],
              ].map(([l, v, fs, color]) => (
                <Box key={l} sx={{ background: tokens.bg2, p: "10px 12px" }}>
                  <Typography sx={{ color: tokens.textDim, fontSize: 9.5, textTransform: "uppercase", letterSpacing: 0.5 }}>{l}</Typography>
                  <Typography sx={{ fontFamily: disp, fontSize: fs || 14, fontWeight: 600, mt: 0.4, color: color || tokens.text }}>{v}</Typography>
                </Box>
              ))}
            </Box>
          </SectionCard>

          {/* Tabs */}
          <SectionCard sx={{ p: 0, height: "auto" }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
              sx={{ borderBottom: `1px solid ${tokens.border}`, minHeight: 44, "& .MuiTab-root": { textTransform: "none", minHeight: 44, fontSize: 13, fontWeight: 600, color: tokens.textDim }, "& .Mui-selected": { color: tokens.textStrong }, "& .MuiTabs-indicator": { backgroundColor: tokens.primary } }}>
              {["Wallet", "In-flight", "KYC", "Activity", "Risk & Responsible Gaming", "Notes"].map((t) => <Tab key={t} label={t} />)}
            </Tabs>

            <Box sx={{ p: 2.25 }}>
              {/* Wallet */}
              {tab === 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,1fr)", lg: "repeat(4,1fr)" }, gap: 1.5 }}>
                  {wallet.map((w) => (
                    <Box key={w.l} sx={{ position: "relative", overflow: "hidden", border: `1px solid ${tokens.border}`, borderRadius: "10px", p: 1.5, background: tokens.bg2 }}>
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, bgcolor: w.bar }} />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                        <Typography sx={{ fontSize: 10.5, color: tokens.textDim, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>{w.l}</Typography>
                        {w.tag && <Box component="span" sx={{ fontFamily: mono, fontSize: 9, fontWeight: 700, px: 0.6, py: "1px", borderRadius: "4px", bgcolor: w.tagBg, color: w.tagC }}>{w.tag}</Box>}
                      </Box>
                      <Typography sx={{ fontFamily: disp, fontSize: 21, fontWeight: 600, mt: 0.6 }}>{w.v}</Typography>
                      <Typography sx={{ fontSize: 11, color: tokens.textDim, mt: 0.3 }}>{w.sub}</Typography>
                      {w.prog != null && (
                        <Box sx={{ height: 6, borderRadius: 4, background: tokens.bg, overflow: "hidden", mt: 0.7 }}>
                          <Box sx={{ height: "100%", width: `${w.prog}%`, background: tokens.pink, borderRadius: 4 }} />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* In-flight */}
              {tab === 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {sel.inflight && sel.inflight.length ? sel.inflight.map(([title, status], i) => (
                    <Box key={i} sx={{ border: `1px solid ${tokens.border}`, borderRadius: "9px", p: 1.3, background: tokens.bg2, display: "flex", alignItems: "center" }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{title}</Typography>
                      <Typography sx={{ ml: "auto", fontFamily: mono, fontSize: 11, color: tokens.textDim }}>{status}</Typography>
                    </Box>
                  )) : <Typography variant="body2" color="text.secondary">No active journeys, missions or loyalty.</Typography>}
                </Box>
              )}

              {/* KYC */}
              {tab === 2 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Chip size="small" label={kp.label} sx={{ bgcolor: kp.bg, color: kp.c, fontWeight: 600 }} />
                  {sel.kyc !== "cleared"
                    ? <Button size="small" variant="outlined" color="inherit" onClick={() => notify("Force KYC — player prompted before next redemption")}>Force KYC</Button>
                    : <Typography sx={{ fontSize: 12, color: tokens.textDim }}>L2 verified · withdrawals enabled</Typography>}
                </Box>
              )}

              {/* Activity */}
              {tab === 3 && (
                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th": { textAlign: "left", color: tokens.textDim, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, p: "9px 10px", borderBottom: `1px solid ${tokens.border}` }, "& td": { p: "10px", borderBottom: `1px solid ${tokens.border}`, fontSize: 12.5 } }}>
                  <thead><tr><th>Time</th><th>Event</th><th>Detail</th></tr></thead>
                  <tbody>
                    {[
                      ["18:32", "game_round", "Gates of Olympus · wager 25"],
                      ["18:30", "gc_purchase", "$50 pack · +500 GC, +5 SC"],
                      ["18:01", "offer_shown", "Big-win celebration · pop-up · clicked"],
                      ["Yest", "sc_redemption_paid", "$120 cash-out"],
                    ].map((r) => (
                      <tr key={r[0] + r[1]}>
                        <td style={{ fontFamily: mono, color: tokens.textDim }}>{r[0]}</td>
                        <td style={{ fontWeight: 600 }}>{r[1]}</td>
                        <td style={{ color: tokens.textDim }}>{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              )}

              {/* Risk */}
              {tab === 4 && (
                String(sel?.id || "").startsWith("PL-") ? (
                  <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: "10px", p: 1.75, background: tokens.bg2 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Risk & Responsible Gaming</Typography>
                    <Typography sx={{ fontSize: 11.5, color: tokens.textDim, mt: 0.5 }}>
                      Risk details load once a real player record is selected.
                    </Typography>
                  </Box>
                ) : (
                  <PlayerRiskCard userId={sel.id} />
                )
              )}

              {/* Notes */}
              {tab === 5 && (
                <Box>
                  {notes.map((n, i) => (
                    <Box key={i} sx={{ border: `1px solid ${tokens.border}`, borderRadius: "9px", p: 1.3, background: tokens.bg2, mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: tokens.primary }}>{n.actor}</Typography>
                        <Typography sx={{ ml: "auto", fontFamily: mono, fontSize: 10.5, color: tokens.textDim }}>{n.time}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>{n.text}</Typography>
                    </Box>
                  ))}
                  <TextField fullWidth size="small" placeholder="Add a note…" multiline minRows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} sx={{ mt: 0.5 }} />
                  <Button size="small" variant="contained" onClick={addNote} sx={{ mt: 1 }}>Save note</Button>
                </Box>
              )}
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Action dialog */}
      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: tokens.surface2, border: `1px solid ${tokens.border}`, borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: disp, fontSize: 16 }}>
          {dialog ? ACTIONS[dialog].title : ""}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>· {sel.name}</Typography>
          <IconButton size="small" onClick={() => setDialog(null)} sx={{ ml: "auto" }}><CloseRoundedIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: tokens.border }}>
          {dialog === "grant" && (
            <>
              <TextField fullWidth size="small" type="number" label="Saga Coins to grant" margin="dense" value={form.amount ?? 25} onChange={(e) => setField("amount", e.target.value)} />
              <TextField fullWidth size="small" label="Reason (required)" margin="dense" placeholder="e.g. goodwill" value={form.reason || ""} onChange={(e) => setField("reason", e.target.value)} />
              <Guard lines={[["State allowed", "pass"], ["No RG suppression", "pass"], ["Within daily budget", "pass"], ["AMOE framing applied", "pass"], ["Frequency cap OK", "pass"]]} />
            </>
          )}
          {dialog === "adjust" && (
            <>
              <TextField select fullWidth size="small" label="Coins" margin="dense" value={form.currency || "Gold Coins"} onChange={(e) => setField("currency", e.target.value)}>
                {["Gold Coins", "Saga Coins"].map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" type="number" label="Adjustment" margin="dense" value={form.amount ?? 100} onChange={(e) => setField("amount", e.target.value)} />
              <TextField fullWidth size="small" label="Reason (required)" margin="dense" value={form.reason || ""} onChange={(e) => setField("reason", e.target.value)} />
              <Maker>Above threshold — requires a second approver (maker–checker).</Maker>
            </>
          )}
          {dialog === "enrol" && (
            <>
              <TextField
                select
                fullWidth
                size="small"
                label="Player type"
                margin="dense"
                value={form.playerType ?? ""}
                onChange={(e) => setField("playerType", e.target.value)}
              >
                {playerTypes.length === 0 && <MenuItem value="" disabled>Loading player types…</MenuItem>}
                {playerTypes.map((pt) => (
                  <MenuItem key={pt.id} value={pt.id}>{pt.player_type}</MenuItem>
                ))}
              </TextField>
              <Guard lines={[["Frequency cap OK", "pass"], ["Updates player_type on enrol", "pass"]]} />
            </>
          )}
          {dialog === "flag" && (
            <>
              <TextField select fullWidth size="small" label="Flag type" margin="dense" value={form.flagType || "Suspected multi-account"} onChange={(e) => setField("flagType", e.target.value)}>
                {["Suspected multi-account", "Bonus abuse", "Velocity anomaly", "Chargeback risk"].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" label="Reason (required)" margin="dense" multiline minRows={2} placeholder="describe the signal" value={form.reason || ""} onChange={(e) => setField("reason", e.target.value)} />
              <Maker>Flagging suppresses all marketing and routes to the risk queue.</Maker>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={() => setDialog(null)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color={dialog ? ACTIONS[dialog].color : "primary"} onClick={submitDialog} disabled={submitting}>
            {submitting ? "Processing…" : dialog ? ACTIONS[dialog].cta : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={3500} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snack?.sev || "success"} variant="filled" onClose={() => setSnack(null)} sx={{ width: "100%" }}>{snack?.text}</Alert>
      </Snackbar>
    </Box>
  );
}
